import { Directive } from '@angular/core';
import { AbstractControlOptions, Validators } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import {
  createLoanPaliersArray,
  withLoanFormValidity,
} from '@app/services/loan/loan-palier.form';
import {
  applyPaliersToLoanPayload,
  hasLoanPaliers,
} from '@app/services/loan/loan-palier.utils';
import { isLoanDiffereSectionActive } from '@app/services/loan/loan-palier.validators';

@Directive()
export abstract class EaSimulationLoanFormBuilder extends PrFormAbstract {
  get simulationStartDate(): Date {
    return new Date(new Date().getFullYear(), 0, 1);
  }

  generate(entity: any) {
    let options: AbstractControlOptions = {};
    const form = this.formBuilder.group(
      {
        mode: [
          this.entity.differe != null || hasLoanPaliers(this.entity)
            ? false
            : true,
          [
            this.prFormService.RefreshEveryValidators([
              'type',
              'insuranceType',
              'insuranceQuotite',
              'differe',
              'durationDiffereInMonth',
            ]),
          ],
        ],

        type: [
          this.entity.type != null ? this.entity.type : 'FIX',
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('mode', false)
                .bind(this),
              Validators.required,
            ),
          ],
        ],

        simuChoice: [
          this.entity.simuChoice != null ? entity.simuChoice : true,
          [
            this.prFormService.RefreshEveryValidators([
              'amountBorrowed',
              'amountMonthly',
            ]),
          ],
        ],
        amountBorrowed: [
          entity ? entity.amountBorrowed : null,
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('simuChoice', true)
                .bind(this),
              Validators.required,
            ),
          ],
        ],
        amountMonthly: [
          entity ? entity.amountMonthly : null,
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('simuChoice', false)
                .bind(this),
              Validators.required,
            ),
          ],
        ],

        durationTotalInMonth: [
          !isNaN(entity.durationTotalInMonth)
            ? entity.durationTotalInMonth
            : 180,
          Validators.required,
        ],
        amountFee: [entity ? entity.amountFee : null, Validators.required],
        interestRate: [
          !isNaN(entity.interestRate) ? entity.interestRate * 100 : 1.5,
          Validators.required,
        ],

        insuranceType: [
          this.entity.insuranceType != null
            ? this.entity.insuranceType
            : 'AMOUNT_BORROWED',
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('mode', false)
                .bind(this),
              Validators.required,
            ),
          ],
        ],
        insuranceRate: [
          !isNaN(entity.insuranceRate) ? entity.insuranceRate * 100 : 0.3,
          Validators.required,
        ],
        insuranceQuotite: [
          !isNaN(entity.insuranceQuotite) ? entity.insuranceQuotite * 100 : 100,
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('mode', false)
                .bind(this),
              Validators.required,
            ),
          ],
        ],

        differe: [
          this.entity.differe != null ? this.entity.differe : 'TOTAL',
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              isLoanDiffereSectionActive,
              Validators.required,
            ),
          ],
        ],
        durationDiffereInMonth: [
          entity.durationDiffereInMonth != null
            ? entity.durationDiffereInMonth
            : 6,
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              isLoanDiffereSectionActive,
              Validators.required,
            ),
          ],
        ],

        paliersEnabled: [hasLoanPaliers(this.entity)],
        paliers: createLoanPaliersArray(this.formBuilder, this.entity.paliers),

        productId: [this.entity.productId != null ? entity.productId : null],

        taeg: [this.entity.taeg != null ? entity.taeg : null],
      },
      options,
    );
    return withLoanFormValidity(form);
  }

  extract() {
    let dto: any = {};

    const date = this.simulationStartDate;

    dto = {
      durationTotalInMonth: this.emptyIsNull(
        this.form.value.durationTotalInMonth,
      ),
      amountFee: this.emptyIsNull(this.form.value.amountFee),
      interestRate: this.emptyIsNull(this.form.value.interestRate / 100),
      insuranceRate: this.emptyIsNull(this.form.value.insuranceRate / 100),
      startDate: date,
      productId: this.emptyIsNull(this.form.value.productId),
    };

    if (this.form.value.simuChoice) {
      dto = Object.assign(dto, {
        amountBorrowed: this.emptyIsNull(this.form.value.amountBorrowed),
      });
    } else {
      dto = Object.assign(dto, {
        amountMonthly: this.emptyIsNull(this.form.value.amountMonthly),
      });
    }

    if (this.form.value.mode === false) {
      dto = Object.assign(dto, {
        type: this.emptyIsNull(this.form.value.type),
        differe: this.emptyIsNull(this.form.value.differe),
        insuranceQuotite: this.emptyIsNull(
          this.form.value.insuranceQuotite / 100,
        ),
        durationDiffereInMonth: this.emptyIsNull(
          this.form.value.durationDiffereInMonth,
        ),
      });

      if (this.form.value.type === 'FIX') {
        dto = Object.assign(dto, {
          insuranceType: this.emptyIsNull(this.form.value.insuranceType),
        });
      }
    }

    return applyPaliersToLoanPayload(dto, this.form.value);
  }
}
